import {
  AddressDTO,
  GetApiSettingsGePatientHistoryOptionsParams,
  StringPaginatedResponseDTO,
} from '@/dtos';
import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import usePrefetchedData from './usePrefetchedData';
import values from 'lodash/values';
import omit from 'lodash/omit';
import compact from 'lodash/compact';
import {
  getApiAddressGetGeoCodeByAddress,
  getApiSettingsGePatientHistoryOptions,
} from '@/endpoints';
import {
  isEmptyString,
} from '@/utils/misc';
import {
  useEffect,
  useRef,
} from 'react';
import isNil from 'lodash/isNil';

export function useSearchPatientHistoryOptions(params: GetApiSettingsGePatientHistoryOptionsParams):
  UseQueryResult<StringPaginatedResponseDTO, Error> {
  return usePrefetchedData<StringPaginatedResponseDTO>({
    key: ['patient-History-options', ...values(omit(params, 'pageSize')) as string[]],
    fetchFn: () => getApiSettingsGePatientHistoryOptions(params),
  });
}

export function useGoogleMapGeoCode(address: string): UseQueryResult<AddressDTO, string> {
  return useQuery<AddressDTO, string>({
    queryKey: ['postalCode', address],
    queryFn: () => {
      return getApiAddressGetGeoCodeByAddress({ address });
    },
    enabled: !isEmptyString(address),
  });
}

export const useGetGooglePlaces = (
  input: string,
): UseQueryResult<Partial<google.maps.places.AutocompletePrediction>[], Error> => {
  const autocompleteSuggestionRef = useRef<
    typeof google.maps.places.AutocompleteSuggestion | null
  >(null);

  // Cache it once before creating the query. (AutocompleteSuggestion is static-only.)
  useEffect(() => {
    if (
      isNil(autocompleteSuggestionRef.current)
      && !isNil(window?.google?.maps?.places?.AutocompleteSuggestion)
    ) {
      autocompleteSuggestionRef.current = window.google.maps.places.AutocompleteSuggestion;
    }
  }, []);

  return useQuery<Partial<google.maps.places.AutocompletePrediction>[], Error>({
    queryKey: ['places', input],
    queryFn: async () => {
      const AutocompleteSuggestion = autocompleteSuggestionRef.current;
      if (isNil(AutocompleteSuggestion)) {
        console.error('Google Places library not loaded');
        throw new Error('Google Places library not loaded');
      }

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['us'],
      });

      return compact((suggestions ?? []).map((suggestion) => {
        const prediction = suggestion.placePrediction;
        const matches = prediction?.mainText?.matches ?? [];

        // Normalize new API result into legacy AutocompletePrediction-like
        // shape used by Address forms and `formatPlace()`.
        return isNil(prediction) ? null : {
          description: prediction.text?.text ?? '',
          place_id: prediction.placeId,
          structured_formatting: {
            main_text: prediction.mainText?.text ?? '',
            secondary_text: prediction.secondaryText?.text ?? '',
            main_text_matched_substrings: matches.map((m) => ({
              offset: m.startOffset,
              length: m.endOffset - m.startOffset,
            })),
          },
        } as Partial<google.maps.places.AutocompletePrediction>;
      }));
    },
    enabled: !isEmptyString(input),
  });
};
